/**
 * ASSET FINANCIAL PASSPORT (UF-06). The persistent record that follows a tokenized asset for life.
 *
 * This is the feature that ties tokenization, the trust, the marketplace, and credit into one thing.
 * Instead of re-applying with documents every time, a sponsor selects an asset whose verified passport
 * the whole ecosystem already trusts: DCPs evaluate it to make offers, and the trust uses the same
 * authoritative record to register collateral and release it when the loan is discharged.
 *
 * It consolidates the asset profile, the trust and holding entity, valuation history, the token supply
 * and treasury, fundraising history, financing and collateral, insurance, income, and compliance.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { api, LipaApiError, type AssetPassport } from '@/lib/api';
import { PageHeader, SectionTitle, StatTile } from '@/components/dash';
import { StatusPill } from '@/components/status';
import { ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';
import { fmtDate } from '@/lib/format';

const num = (n: number) => n.toLocaleString('en-KE');

export default function Passport() {
  // React Router types a param as possibly absent; this route cannot match without one.
  const { assetId = '' } = useParams();
  const [p, setP] = useState<AssetPassport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setP(await api.finance.passport(assetId));
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load this passport.');
    }
  }, [assetId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!p) return <LoadingCard lines={5} />;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link
        to="/credit"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <BackIcon /> Credit
      </Link>

      <PageHeader
        title={p.name}
        subtitle={`${p.type} · ${p.location}`}
        actions={
          <Link
            to={`/credit/apply?asset=${p.id}`}
            className="rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:bg-primary-light"
          >
            Request financing
          </Link>
        }
      />

      {/* PASSPORT HEADER: this is a trusted record, so say so. */}
      <Reveal>
        <section className="rounded-card border border-primary/20 bg-primary-tint p-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-brand">
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.7}
                  d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
              </svg>
            </span>
            <p className="text-sm text-ink">
              This is a verified financial passport. Providers and the trust rely on it, so you
              never re-apply with documents from scratch.
            </p>
          </div>
        </section>
      </Reveal>

      {/* TRUST + HOLDING. */}
      <section className="rounded-card border border-border bg-surface p-5 shadow-card">
        <SectionTitle>Trust and holding structure</SectionTitle>
        <dl className="divide-y divide-border text-sm">
          <Row k="Holding entity" v={p.trust.holdingEntity} />
          <Row k="Trust" v={p.trust.name ?? 'Assigned after formation'} />
          <Row k="Trust number" v={p.trust.trustNumber ?? '-'} />
        </dl>
      </section>

      {/* VALUATION HISTORY. */}
      <section>
        <SectionTitle>Valuation history</SectionTitle>
        <ul className="overflow-hidden rounded-card border border-border bg-surface shadow-card">
          {p.valuationHistory.map((v, i) => (
            <li
              key={i}
              className={`flex items-center justify-between gap-3 px-4 py-3 ${i > 0 ? 'border-t border-border' : ''}`}
            >
              <div>
                <p className="tabular text-sm font-medium text-ink">KES {v.value}</p>
                <p className="text-micro text-ink-subtle">{v.valuer}</p>
              </div>
              <span className="text-micro text-ink-subtle">{fmtDate(v.at)}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* SUPPLY + FUNDRAISING. */}
      {p.supply ? (
        <section>
          <SectionTitle>Token supply and fundraising</SectionTitle>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile label="Total supply" value={num(p.supply.total)} />
            <StatTile label="Token price" value={`KES ${p.supply.price}`} />
            <StatTile label="Listings" value={p.listings} />
            <StatTile label="Capital raised" value={`KES ${p.capitalRaised}`} accent />
          </div>
        </section>
      ) : null}

      {/* FINANCING. */}
      <section>
        <SectionTitle>Financing</SectionTitle>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label="Loans" value={p.financing.loans} />
          <StatTile label="Outstanding" value={`KES ${p.financing.outstanding}`} />
          <StatTile label="Max borrowing" value={`KES ${p.financing.maxBorrowing}`} />
          <StatTile label="Available equity" value={`KES ${p.financing.availableEquity}`} accent />
        </div>
      </section>

      {/* COLLATERAL. */}
      {p.collateral.length > 0 ? (
        <section>
          <SectionTitle>Current collateral</SectionTitle>
          <ul className="space-y-2">
            {p.collateral.map((c) => (
              <li
                key={c.loanId}
                className="flex items-center justify-between gap-3 rounded-card border border-border bg-surface px-4 py-3 shadow-card"
              >
                <div>
                  <p className="text-sm font-medium text-ink">{c.provider}</p>
                  <p className="tabular text-micro text-ink-subtle">
                    {num(c.tokensLocked)} tokens · LTV {c.ltvPct}%
                  </p>
                </div>
                <StatusPill
                  status={c.status}
                  label={c.status === 'locked' ? 'Locked' : undefined}
                />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* INSURANCE + COMPLIANCE. */}
      <section className="rounded-card border border-border bg-surface p-5 shadow-card">
        <SectionTitle>Insurance and compliance</SectionTitle>
        <p className="text-sm text-ink-muted">{p.insurance}</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {p.compliance.map((c) => (
            <span
              key={c}
              className="inline-flex items-center gap-1 rounded-pill bg-accent/10 px-2 py-0.5 text-micro font-medium text-accent"
            >
              <CheckIcon /> {c}
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <dt className="text-ink-muted">{k}</dt>
      <dd className="tabular text-right font-medium text-ink">{v}</dd>
    </div>
  );
}

function BackIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="m5 13 4 4L19 7" />
    </svg>
  );
}
